import { loadStripe } from '@stripe/stripe-js';
import { callCloudFunction } from '../../../firebase/callable';

let stripePromise;

/**
 * Lazily initializes Stripe.js.
 * @returns {Promise<import('@stripe/stripe-js').Stripe | null>}
 */
function getStripe() {
  if (!stripePromise) {
    const publishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
    stripePromise = loadStripe(publishableKey || '');
  }

  return stripePromise;
}

/**
 * Starts a Stripe Checkout purchase for a product.
 * Redirects with `url` when available, otherwise falls back to `sessionId`.
 * @param {string} productId Product ID to buy.
 * @returns {Promise<void>}
 */
export async function startStripeCheckout(productId, licenseAcceptance) {
  const result = await callCloudFunction('createStripeCheckoutSession', { productId, ...(licenseAcceptance ? { licenseAcceptance } : {}) });

  if (result?.url) {
    window.location.assign(result.url);
    return;
  }

  if (!result?.sessionId) {
    throw new Error('Stripe checkout session was not returned by backend.');
  }

  const stripe = await getStripe();
  if (!stripe) {
    throw new Error('Stripe could not be initialized. Check REACT_APP_STRIPE_PUBLISHABLE_KEY.');
  }

  const redirectResult = await stripe.redirectToCheckout({ sessionId: result.sessionId });
  if (redirectResult.error) {
    throw redirectResult.error;
  }
}

/**
 * Loads Stripe price display details for a product.
 * @param {string} productId Product ID.
 * @returns {Promise<{productId: string, priceId: string, currency: string, unitAmount: number, active: boolean, livemode: boolean, type: string}>}
 */
export async function getStripeProductPrice(productId) {
  return callCloudFunction('getStripeProductPrice', { productId });
}
